import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation } from '../useApiMutation';
import { apiPost, operationsClient } from '@/lib/api';
import { opsProposalEndpoints } from '@/lib/api/endpoints';
import { opsProposalKeys } from '@/lib/queryKeys';
import type { Proposal } from '@/types/proposal';

/** Typed name plus the signature pad's PNG data URL. */
export interface InPersonSignature {
  name: string;
  image: string;
}

export interface AcceptInPersonInput {
  id: string;
  optionIndex: number;
  expectedTotal?: number;
  lineItems?: { lineItemId: string; lineKey?: string; isSelected: boolean }[];
  signature?: InPersonSignature | null;
  acceptMembershipOffer?: boolean;
  membershipBillingOptionId?: string | null;
}

interface AcceptInPersonResult {
  /** The customer's projection — the same shape the presentation screen reads. */
  proposal: Proposal;
  estimateId: string;
}

/**
 * Presentation mode's accept: the customer picked and signed on the staff
 * device. The request body is the same acceptance contract the customer's own
 * page posts — the server records it identically and returns the born-APPROVED
 * estimate it created.
 */
export function useAcceptProposalInPerson() {
  const queryClient = useQueryClient();

  return useApiMutation<AcceptInPersonResult, AcceptInPersonInput>({
    mutationFn: ({
      id,
      optionIndex,
      expectedTotal,
      lineItems,
      signature,
      acceptMembershipOffer,
      membershipBillingOptionId,
    }) =>
      apiPost<AcceptInPersonResult>(operationsClient, opsProposalEndpoints.acceptInPerson(id), {
        option_index: optionIndex,
        ...(expectedTotal != null ? { expectedTotal } : {}),
        ...(lineItems?.length ? { lineItems } : {}),
        terms_accepted: true,
        ...(signature
          ? {
              customer_signature_name: signature.name,
              customer_signature_image: signature.image,
            }
          : {}),
        ...(acceptMembershipOffer ? { acceptMembershipOffer: true } : {}),
        ...(acceptMembershipOffer && membershipBillingOptionId
          ? { membershipBillingOptionId }
          : {}),
      }),
    onSuccess: (data) => {
      // The presenting screen reads the presentation query, so the accepted
      // proposal goes straight into it. Without this, exiting and reopening
      // within the app's 60s staleTime replays the cached pre-accept payload
      // and offers the customer controls for a decision they already made.
      queryClient.setQueryData(opsProposalKeys.presentation(data.proposal.id), data.proposal);
      void queryClient.invalidateQueries({ queryKey: opsProposalKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: opsProposalKeys.detail(data.proposal.id) });
    },
  });
}
